import { Injectable } from '@angular/core';

import {
  SetStoreEntry,
  SetStoreKey,
  SetStoreKeyType,
  StoreEntry,
  StoreKey,
  StoreKeyDefault,
  StoreKeyType,
} from '@plugins/middleware-api';
import { ScriptService } from '@plugins/script';
import {
  Override,
  PlatformFeatures,
  PlatformPermission,
} from '@plugins/shared/types/auth';

import type { Language, MiddlewareSDK, MiddlewareStore, UserToken } from '..';

declare global {
  interface Window {
    eepSDK: new (cfg: unknown) => MiddlewareSDK;
    __middlewareSDK: MiddlewareSDK;
  }
}

@Injectable({ providedIn: 'root' })
export class MiddlewareSDKProxy implements MiddlewareSDK {
  private _sdk!: Promise<MiddlewareSDK>;
  public store: MiddlewareStore;

  constructor(private script: ScriptService) {
    // re-use pre-initialized sdk or load fresh
    this._sdk = window.__middlewareSDK
      ? Promise.resolve<MiddlewareSDK>(window.__middlewareSDK)
      : this.load();
    this.store = new MiddlewareStoreProxy(this._sdk);
  }

  private load(): Promise<MiddlewareSDK> {
    return new Promise((resolve, reject) => {
      const jwt =
        new URL(window.location.href).searchParams.get('jwt') ||
        document.documentElement.dataset['eepmToken'];
      if (!jwt) {
        return reject(new Error('No plugin jwt found.'));
      }
      const { platform } = JSON.parse(
        decodeURIComponent(
          window.atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
        ),
      ) as UserToken;

      const { name, sdk } = platform;

      document.documentElement.classList.add(`platform-${name}`);

      this.script.loadScript(sdk.v1).subscribe(() => {
        // set into window scope to propagate instance into all lazyloaded modules
        window.__middlewareSDK = new window.eepSDK(sdk.config);
        resolve(window.__middlewareSDK);
      });
    });
  }

  async initHeightObserver(options?: {
    min?: number | undefined;
    max?: number | undefined;
    selector?: string | undefined;
    interval?: number | undefined;
  }): Promise<void> {
    return (await this._sdk).initHeightObserver(options);
  }
  async isMobile(): Promise<boolean> {
    return (await this._sdk).isMobile();
  }
  async isIos(): Promise<boolean> {
    return (await this._sdk).isIos();
  }
  async isAndroid(): Promise<boolean> {
    return (await this._sdk).isAndroid();
  }
  async isNative(): Promise<boolean> {
    return (await this._sdk).isNative();
  }
  async openLink(url: string): Promise<void> {
    return (await this._sdk).openLink(url);
  }
  async openLinkExternal(url: string): Promise<void> {
    return (await this._sdk).openLinkExternal(url);
  }
  async openLinkInternal(url: string): Promise<void> {
    return (await this._sdk).openLinkInternal(url);
  }
  async getBrowserLanguage(): Promise<Language> {
    return (await this._sdk).getBrowserLanguage();
  }
  async getAvailableLanguages(): Promise<Language[]> {
    return (await this._sdk).getAvailableLanguages();
  }
  async getDefaultLanguage(): Promise<Language> {
    return (await this._sdk).getDefaultLanguage();
  }
  async getPreferredLanguage(options?: string[]): Promise<string> {
    return (await this._sdk).getPreferredLanguage(options);
  }
  async openNativeFileDialog(event: Event): Promise<File> {
    return (await this._sdk).openNativeFileDialog(event);
  }
  async setWindowHeight(height: number) {
    return (await this._sdk).setWindowHeight(height);
  }
  async getFeatures(): Promise<PlatformFeatures> {
    return (await this._sdk).getFeatures();
  }
  async isFeatureAvailable(feature: keyof PlatformFeatures): Promise<boolean> {
    return (await this._sdk).isFeatureAvailable(feature);
  }
  async isFeatureDisabled(feature: keyof PlatformFeatures): Promise<boolean> {
    return (await this._sdk).isFeatureDisabled(feature);
  }
  async isFeatureEnabled(feature: keyof PlatformFeatures): Promise<boolean> {
    return (await this._sdk).isFeatureEnabled(feature);
  }
  async isOverridePresent(override: Override): Promise<boolean> {
    return (await this._sdk).isOverridePresent(override);
  }
  async permissionsAvailable(perms: PlatformPermission[]): Promise<boolean> {
    return (await this._sdk).permissionsAvailable(perms);
  }
  async onSave(
    cb: (respond: (data: boolean) => void) => void,
  ): Promise<{ hideControls: boolean; disconnect: () => void }> {
    return (await this._sdk).onSave(cb);
  }
}

class MiddlewareStoreProxy implements MiddlewareStore {
  constructor(private _sdk: Promise<MiddlewareSDK>) {}

  async get<T>(type: StoreKeyType, key = StoreKeyDefault): Promise<T> {
    return (await this._sdk).store.get(type, key);
  }
  async mget<T>(keys: StoreKey[]): Promise<StoreEntry<T>[]> {
    return (await this._sdk).store.mget(keys);
  }
  async set<T>(type: SetStoreKeyType, value: T): Promise<void> {
    return (await this._sdk).store.set(type, value);
  }
  async mset<T>(entries: SetStoreEntry<T>[]): Promise<void> {
    return (await this._sdk).store.mset(entries);
  }
  async delete(type: SetStoreKeyType, key = StoreKeyDefault): Promise<void> {
    return (await this._sdk).store.delete(type, key);
  }
  async mdelete(keys: SetStoreKey[]): Promise<void> {
    return (await this._sdk).store.mdelete(keys);
  }
  async list<T>(type: StoreKeyType[]): Promise<StoreEntry<T>[]> {
    return (await this._sdk).store.list(type);
  }
}
